/**
 * Default preference values
 * Centralized location for all preference defaults to ensure consistency
 * across the application and make it easy to update defaults
 */

export const PREF_DEFAULTS = {
  /**
   * UI Preferences (user-scoped)
   */
  UI: {
    SIDEBAR_COLLAPSED: false,
    THEME_MODE: 'dark' as const,
    COLLECTIONS_FILTER_OPEN: true, // Default to open for better UX
    DENSITY: 'comfortable' as const,
  },

  /**
   * View Mode Preferences (user-scoped)
   */
  VIEW: {
    MODELS_MODE: 'grid' as const,
    JOBS_MODE: 'list' as const,
    PRINTERS_MODE: 'grid' as const,
    SPOOLS_MODE: 'table' as const,
  },

  /**
   * Sort Preferences (user-scoped)
   */
  SORT: {
    MODELS_COLUMN: 'addedAt' as const,
    MODELS_ORDER: 'desc' as const,
    JOBS_COLUMN: 'createdAt' as const,
    JOBS_ORDER: 'desc' as const,
    SPOOLS_COLUMN: 'purchaseDate' as const,
    SPOOLS_ORDER: 'desc' as const,
  },

  /**
   * Filter Preferences (user-scoped)
   */
  FILTER: {
    MODELS_SELECTED_COLLECTIONS: [] as string[],
    MODELS_FAVORITES_ONLY: false,
    JOBS_STATUS: '' as const,
  },

  /**
   * Notification Preferences (user-scoped)
   */
  NOTIFICATIONS: {
    EMAIL_LOW_FILAMENT: true,
    EMAIL_JOB_COMPLETE: true,
    EMAIL_JOB_FAILED: true,
    IN_APP_ENABLED: true,
  },

  /**
   * Workspace Settings (workspace-scoped)
   */
  WORKSPACE: {
    TIMEZONE: 'UTC' as const,
    DATE_FORMAT: 'yyyy-MM-dd' as const,
    UNITS: 'metric' as const,
  },

  /**
   * Default Values (workspace-scoped)
   */
  DEFAULTS: {
    PRINTER_BRAND: '' as const,
    FILAMENT_MANUFACTURER: '' as const,
    FILAMENT_TYPE: '' as const,
  },
} as const;

/**
 * Helper type to extract the type of a preference default
 */
export type PreferenceDefault<T> = T extends (typeof PREF_DEFAULTS)[keyof typeof PREF_DEFAULTS][keyof (typeof PREF_DEFAULTS)[keyof typeof PREF_DEFAULTS]]
  ? T
  : never;
